import React from 'react';
import { Alert, Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { ROUTES } from '../routes';

function parseHashParams() {
  const raw = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
  return new URLSearchParams(raw);
}

function safeParseJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { setSession, user } = useAuth();
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const delayRef = React.useRef(null);

  React.useEffect(() => {
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }

    const params = parseHashParams();
    const err = params.get('error');
    const token = params.get('token');
    const role = params.get('role');
    const enterpriseId = params.get('enterprise_id');
    const expiresIn = params.get('expires_in');
    const expiresAt = params.get('expires_at');
    const returnTo = params.get('returnTo');
    const sessionPayload = safeParseJson(params.get('session'));

    console.log('[itam] oauth callback raw', {
      hash: window.location.hash,
      token,
      role,
      enterpriseId,
      expiresIn,
      expiresAt,
      returnTo,
      sessionPayload,
      existingUser: user,
    });

    if (!err && !token && !role && !sessionPayload && user) {
      setLoading(false);
      const fallback =
        user.role === 'super' || user.role === 'superadmin'
          ? ROUTES.SUPERADMIN
          : user.role === 'partner'
            ? ROUTES.PARTNER
            : ROUTES.ENTERPRISE;
      navigate(fallback, { replace: true });
      return;
    }

    if (err) {
      setLoading(false);
      setError(err);
      return;
    }

    const nextSession = {
      token: token || sessionPayload?.token || null,
      role: role || sessionPayload?.role || null,
      enterprise_id: enterpriseId ? Number(enterpriseId) : sessionPayload?.enterprise?.id || sessionPayload?.enterprise_id || null,
      enterprise_name: sessionPayload?.enterprise?.name || sessionPayload?.enterprise_name || '',
      full_name: sessionPayload?.user?.full_name || sessionPayload?.full_name || '',
      page_access: Array.isArray(sessionPayload?.user?.page_access)
        ? sessionPayload.user.page_access
        : Array.isArray(sessionPayload?.page_access)
          ? sessionPayload.page_access
          : [],
      must_change_password: Boolean(
        sessionPayload?.user?.must_change_password ?? sessionPayload?.must_change_password
      ),
      password_changed: Boolean(sessionPayload?.user?.password_changed ?? sessionPayload?.password_changed),
      expires_in: expiresIn || sessionPayload?.expires_in || '7d',
      expires_at: expiresAt || sessionPayload?.expires_at || null,
    };

    if (!nextSession.token || !nextSession.role) {
      console.log('[itam] oauth payload pending', {
        token,
        role,
        sessionPayload,
        params: Object.fromEntries(params.entries()),
      });
      delayRef.current = setTimeout(() => {
        setLoading(false);
        setError('oauth_payload_missing');
      }, 600);
      return;
    }

    console.log('[itam] oauth callback next session', nextSession);

    setSession(nextSession);
    setLoading(false);

    const fallback =
      nextSession.role === 'super' || nextSession.role === 'superadmin'
        ? ROUTES.SUPERADMIN
        : nextSession.role === 'partner'
          ? ROUTES.PARTNER
          : ROUTES.ENTERPRISE;
    const safeReturnTo = returnTo && returnTo.startsWith('/') ? returnTo : fallback;
    navigate(safeReturnTo, { replace: true });
  }, [navigate, setSession, user]);

  React.useEffect(() => () => {
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#F8FAFC', p: 2 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {error ? (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Sign-in failed
              </Typography>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Please go back to login and try again.
              </Typography>
            </>
          ) : loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={22} />
              <Typography variant="body1">Completing sign-in...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={22} />
              <Typography variant="body1">Redirecting...</Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
